if (!customElements.get('back-in-stock')) {
  class BackInStock extends HTMLElement {
    static companyId = 'HvSe36';
    static emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    constructor() {
      super();

      this.form = this.querySelector('.back-in-stock__form');
      this.input = this.querySelector('.back-in-stock__input');
      this.error = this.querySelector('.back-in-stock__error');
      this.submitButton = this.querySelector('.back-in-stock__submit');
      this.spinner = this.submitButton.querySelector('.loading__spinner');
      this.message = this.querySelector('.back-in-stock__message');
      this.abortController = null;

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleInput = this.handleInput.bind(this);
    }

    connectedCallback() {
      this.form.addEventListener('submit', this.handleSubmit);
      this.input.addEventListener('input', this.handleInput);
    }

    disconnectedCallback() {
      this.form.removeEventListener('submit', this.handleSubmit);
      this.input.removeEventListener('input', this.handleInput);
      this.abortController?.abort();
    }

    updateVariant(variantId) {
      if (!variantId || variantId === this.dataset.variantId) return;

      this.abortController?.abort();
      this.abortController = null;
      this.dataset.variantId = variantId;
      this.setPending(false);
      this.clearError();
      this.clearMessage();
    }

    handleInput() {
      if (this.input.getAttribute('aria-invalid') === 'true') this.clearError();
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.abortController) return;

      const email = this.input.value.trim();
      const variantId = this.dataset.variantId;

      this.clearMessage();

      if (!BackInStock.emailPattern.test(email)) {
        this.showError(this.dataset.errorEmail);
        return;
      }

      this.clearError();
      this.setPending(true);
      const abortController = new AbortController();
      this.abortController = abortController;

      try {
        await this.subscribe(email, variantId, abortController.signal);
        this.input.value = '';
        this.message.textContent = this.dataset.success;
        this.toggleAttribute('data-subscribed', true);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Back in stock subscription failed:', error);
        this.message.classList.add('back-in-stock__message--error');
        this.message.textContent = this.dataset.errorService;
      } finally {
        if (this.abortController === abortController) {
          this.abortController = null;
          this.setPending(false);
        }
      }
    }

    async subscribe(email, variantId, signal) {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${BackInStock.companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(this.buildPayload(email, variantId)),
          signal,
        }
      );

      if (response.status !== 202) throw new Error(`Unexpected Klaviyo response status ${response.status}`);
    }

    buildPayload(email, variantId) {
      return {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };
    }

    setPending(isPending) {
      this.submitButton.toggleAttribute('disabled', isPending);
      this.submitButton.classList.toggle('loading', isPending);
      this.submitButton.setAttribute('aria-busy', String(isPending));
      this.spinner?.classList.toggle('hidden', !isPending);
    }

    showError(text) {
      this.error.textContent = text;
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
    }

    clearMessage() {
      this.removeAttribute('data-subscribed');
      this.message.textContent = '';
      this.message.classList.remove('back-in-stock__message--error');
    }

    clearError() {
      this.error.textContent = '';
      this.error.hidden = true;
      this.input.removeAttribute('aria-invalid');
    }
  }

  customElements.define('back-in-stock', BackInStock);
}
